import { useEffect, useState } from 'react'
import AlertMessage from '@components/atoms/alert-message'
import Button from '@components/atoms/button'
import { CompetitionTimer } from '@components/competitions/competition-timer'
import { useSessionContext } from '@contexts/session'
import { CALENDAR_IMAGE_PATH } from '@lib/constants/teacher-dashboard'
import { FastlyImage } from '@components/common/image'
import { CClient } from '@common/utils'
import { captureException } from '@sentry/node'
import date, {
  SYSTEM_TIMEZONE,
  getCurrentAbbreviatedTimeZone,
} from '@lib/utils/date.utils'
type SrcRescheduleCooldown = {
  availableAt: string | null
}

type Props = {
  start: string
  onYesClick: () => void
  onNoClick: () => void
  lessonNumber: string | number
  lessonTitle: string
  rescheduleRequestedBy: 'student' | 'teacher' | null
  setRescheduleRequestedBy: (value: 'student' | 'teacher' | null) => void
}

const studentConfirmations = {
  'student-confirm1':
    'I confirm the student/parent has informed me to reschedule this class.',
  'student-confirm2':
    'I confirm a discussion about this occurred on the WhatsApp group.',
  'student-confirm3':
    "I've informed students they can only cancel up to two classes monthly and should do so only in emergencies.",
  'student-confirm4':
    'I understand an automated message will be sent to the student: "Your teacher informed us that you have requested to cancel your upcoming class."',
  'student-confirm5':
    'I confirm I am not exploiting the system, and I understand penalties may apply for any wrongdoing.',
}

const teacherConfirmations = {
  'teacher-confirm1':
    'I have notified them about this cancellation on WhatsApp group to students & parents.',
  'teacher-confirm2':
    "I understand the impact on the student's learning journey and will try to provide a substitute class with another teacher or myself as soon as possible.",
  'teacher-confirm3':
    'I know the class cancellation policy: penalties range from INR 300 to INR 500 per class, and frequent teacher cancellations may lead to strict action from Codingal.',
}

const CheckIfStudentIsInformed = ({
  start,
  onNoClick,
  onYesClick,
  lessonNumber,
  lessonTitle,
  rescheduleRequestedBy,
  setRescheduleRequestedBy,
}: Props) => {
  const { name, timezone, teacher } = useSessionContext()
  // When the teacher can next choose "Student requested". The backend enforces
  // the same cooldown, this only disables the option up front.
  const [srcAvailableAt, setSrcAvailableAt] = useState<string | null>(null)
  // Moving a class to a time before the cooldown ends is still allowed, which
  // the backend checks against the picked time.
  const [isMovingWithinSrcCooldown, setIsMovingWithinSrcCooldown] = useState(
    false
  )
  const isStudentReasonDisabled = !!srcAvailableAt && !isMovingWithinSrcCooldown
  const [confirmations, setConfirmations] = useState({
    student: Object.keys(studentConfirmations).map(() => false),
    teacher: Object.keys(teacherConfirmations).map(() => false),
  })

  const handleConfirmationChange = (
    type: 'student' | 'teacher',
    index: number
  ) => {
    setConfirmations((prev) => {
      const newConfirmations = { ...prev }
      newConfirmations[type][index] = !newConfirmations[type][index]
      return newConfirmations
    })
  }

  const allConfirmationsChecked = (type: 'student' | 'teacher') => {
    return confirmations[type].every((item) => item)
  }

  useEffect(() => {
    const getSrcRescheduleCooldown = async () => {
      const url = `/api/v1/teachers/${teacher.id}/src_reschedule_cooldown/`
      try {
        const response = await CClient(url)
        if (!response.ok) throw new Error(`${url} failed: ${response.status}`)
        const data = (await response.json()) as SrcRescheduleCooldown
        if (data.availableAt && date().isBefore(data.availableAt))
          setSrcAvailableAt(data.availableAt)
      } catch (error) {
        // Leave the option enabled, the backend still blocks it if needed.
        captureException(error)
      }
    }
    getSrcRescheduleCooldown()
  }, [])

  // Clear a "Student requested" choice made before the cooldown loaded, or
  // when the teacher unticks moving the class within the cooldown.
  useEffect(() => {
    if (isStudentReasonDisabled && rescheduleRequestedBy === 'student')
      setRescheduleRequestedBy(null)
  }, [isStudentReasonDisabled, rescheduleRequestedBy])

  useEffect(() => {
    if (!srcAvailableAt) return
    const timeout = setTimeout(
      () => setSrcAvailableAt(null),
      date(srcAvailableAt).diff(date())
    )
    return () => clearTimeout(timeout)
  }, [srcAvailableAt])

  const userTimezone = timezone || SYSTEM_TIMEZONE
  const srcAvailableAtTime =
    srcAvailableAt &&
    `${date(srcAvailableAt)
      .tz(userTimezone)
      .format('h:mm A')} ${getCurrentAbbreviatedTimeZone(userTimezone)}`

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex items-center justify-center">
        <FastlyImage
          src={`${CALENDAR_IMAGE_PATH}calendar-reschedule.png`}
          width={60}
          height={60}
        />
      </div>
      <div className="space-y-2">
        <p className="text-lg leading-tight font-600">
          You are rescheduling Lesson{lessonNumber && ` ${lessonNumber}`}:{' '}
          {lessonTitle}
        </p>
        <p className="leading-tight text-grey-800">
          Hey {name},{' '}
          <span className="font-600">
            {' '}
            only <CompetitionTimer endTime={start} className="font-600" /> left
          </span>{' '}
          for the class to start. Class cancellation might degrade the
          student&apos;s experience.
        </p>
      </div>
      <div className="space-y-3">
        <p className="text-lg leading-tight font-600 -mt-px">
          Who requested to reschedule this class?
        </p>
        <AlertMessage type="info" className="leading-tight">
          <span aria-hidden="true" className="mr-1">
            💡
          </span>
          <span className="font-600">Need a day off?</span> You can apply for
          leave instead. Our team will take care of your students&apos; classes,
          and you won&apos;t get a cancellation penalty.
        </AlertMessage>
        <div className="space-y-2">
          <div className="flex space-x-2">
            <input
              type="radio"
              id="student"
              name="rescheduleBy"
              value="student"
              checked={rescheduleRequestedBy === 'student'}
              onChange={() => setRescheduleRequestedBy('student')}
              disabled={isStudentReasonDisabled}
            />
            <label
              htmlFor="student"
              className={`${
                rescheduleRequestedBy === 'student' ? 'font-600' : ''
              } ${
                isStudentReasonDisabled
                  ? 'text-grey-700 cursor-not-allowed'
                  : ''
              }`}
            >
              Student requested to reschedule
            </label>
            {isStudentReasonDisabled && (
              <span className="inline-flex items-center rounded border border-red-200 px-2 py-0.5 text-xs font-600 text-red-200">
                Disabled
              </span>
            )}
          </div>
          {srcAvailableAt && (
            <div className="pl-6 bg-grey-50 border border-grey-200 rounded-md p-3 space-y-3">
              {!isMovingWithinSrcCooldown && (
                <p className="text-sm text-grey-800 leading-relaxed">
                  You recently rescheduled a class as student requested. You can
                  choose this option again at{' '}
                  <span className="font-600 text-orange">
                    {srcAvailableAtTime}
                  </span>{' '}
                  (in{' '}
                  <CompetitionTimer
                    endTime={srcAvailableAt}
                    className="font-600 text-orange"
                  />
                  ). If this class starts before then, join the class and end it
                  with the reason &quot;Student informed in advance they cannot
                  join&quot;.
                </p>
              )}
              <label className="flex items-start space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isMovingWithinSrcCooldown}
                  onChange={() =>
                    setIsMovingWithinSrcCooldown(!isMovingWithinSrcCooldown)
                  }
                  className="form-checkbox cursor-pointer h-4 w-4 mt-1 flex-shrink-0"
                />
                <span className="text-sm text-grey-800 leading-relaxed">
                  <span className="font-600 text-grey-900">
                    I&apos;m moving this class to a time before{' '}
                    {srcAvailableAtTime}
                  </span>{' '}
                  (for example, into a slot another student just freed up)
                </span>
              </label>
              {isMovingWithinSrcCooldown && (
                <p className="text-sm text-grey-800 leading-relaxed">
                  On the next step, choose &quot;I want to reschedule this class
                  at another time&quot; and pick a time before{' '}
                  <span className="font-600 text-orange">
                    {srcAvailableAtTime}
                  </span>
                  . Any other time can&apos;t be saved as student requested
                  until then.
                </p>
              )}
            </div>
          )}
          {rescheduleRequestedBy === 'student' && (
            <>
              <div className="pl-6 bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                <p className="text-sm text-yellow-800 font-500 mb-1">
                  Reason selected: Student requested to cancel
                </p>
                <p className="text-sm text-yellow-700 leading-relaxed">
                  If the reason &ldquo;Student requested to reschedule&rdquo; is
                  selected incorrectly, a strike may be issued for misuse of the
                  rescheduling feature and violation of platform integrity.
                  Please choose this reason only if the student explicitly
                  requested the reschedule.
                </p>
                <p className="text-sm text-yellow-800 font-600 leading-relaxed mt-2">
                  After this, you won&apos;t be able to reschedule another class
                  as student requested for the next 45 minutes, from the
                  dashboard or the reschedule course tool.
                </p>
              </div>
              <div
                className="pl-6 space-y-2 thin-scroll-bar"
                style={{ maxHeight: '195px', overflowY: 'auto' }}
              >
                {Object.entries(studentConfirmations).map(
                  ([id, text], index) => (
                    <div key={id} className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id={id}
                        checked={confirmations.student[index]}
                        onChange={() =>
                          handleConfirmationChange('student', index)
                        }
                        className="form-checkbox cursor-pointer h-4 w-4 mt-1"
                      />
                      <label htmlFor={id} className="leading-tight">
                        {text}
                      </label>
                    </div>
                  )
                )}
              </div>
            </>
          )}
          <div
            className={`flex space-x-2 ${
              rescheduleRequestedBy === 'student' ? 'pt-2' : ''
            }`}
          >
            <input
              type="radio"
              id="teacher"
              name="rescheduleBy"
              value="teacher"
              checked={rescheduleRequestedBy === 'teacher'}
              onChange={() => setRescheduleRequestedBy('teacher')}
            />
            <label
              htmlFor="teacher"
              className={rescheduleRequestedBy === 'teacher' ? 'font-600' : ''}
            >
              Teacher requested to reschedule
            </label>
          </div>
          {rescheduleRequestedBy === 'teacher' && (
            <div
              className="pl-6 space-y-2 thin-scroll-bar"
              style={{ maxHeight: '195px', overflowY: 'auto' }}
            >
              {Object.entries(teacherConfirmations).map(([id, text], index) => (
                <div key={id} className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id={id}
                    checked={confirmations.teacher[index]}
                    onChange={() => handleConfirmationChange('teacher', index)}
                    className="form-checkbox cursor-pointer h-4 w-4 mt-1"
                  />
                  <label htmlFor={id}>{text}</label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-xl leading-tight font-600">
          Is the student aware of this rescheduling?
        </p>
        <div className="flex space-x-5">
          <Button
            onClick={onYesClick}
            disabled={
              !rescheduleRequestedBy ||
              !allConfirmationsChecked(rescheduleRequestedBy)
            }
            className="text-sm"
          >
            Yes, the student is aware
          </Button>
          <button
            onClick={onNoClick}
            className="font-600 text-orange hover:underline focus:outline-none text-sm"
          >
            No, student is not aware
          </button>
        </div>
      </div>
    </div>
  )
}

export default CheckIfStudentIsInformed
